/**
 * ⭐ `AW19` · **Partir el hilo en SESIONES de 24 h, y decir en qué estado está la
 * actual.** Funciones puras, sin React y sin reloj propio.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔ ① LO QUE ESTE ARCHIVO **NO** HACE, Y ES SU REQUISITO PRINCIPAL
 * ════════════════════════════════════════════════════════════════════════════
 *
 * **No bloquea nada.** No hay acá —ni tiene que haber en quien lo consuma— una
 * función que conteste «no se puede continuar».
 *
 * `modules/conversation/src/sesion/ventana.ts` es explícito:
 *
 *     if (transcurrido > ventanaDeSesionMs) return { caso: "nueva" }
 *
 * **Pasada la ventana no se rechaza nada**: se abre una sesión **nueva**, y el
 * `id_conversacion` —el hilo largo— **no cambia**. `gates/conversation.spec.ts` lo
 * ejerce con reloj plantado: dos mensajes separados por media ventana comparten
 * `id_sesion`; pasada la ventana entera, el siguiente abre otro.
 *
 * ⇒ **desde el lado del participante siempre se puede escribir.** La regla de las
 * 24 h de WhatsApp aplica a lo que el **negocio** inicia, y fuera de ventana exige
 * **plantilla**: eso es `CH5`, `TemplateContent` está **declarada y sin ejercer**.
 * Una pantalla que deshabilitara el campo estaría **inventando una regla que el
 * sistema no tiene**, y el operador estaría probando la pantalla en vez del motor —
 * que es exactamente lo que `ADR-0196` dice que el simulador existe para evitar.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔ ② EL RELOJ ENTRA POR ARGUMENTO. SIEMPRE.
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Ninguna función de acá lee `Date.now()`. El borde exacto de la ventana es **el
 * único punto donde este cálculo importa**, y con el reloj leído adentro sólo se
 * puede examinar parcheando un global — que es la clase de examen que después nadie
 * toca. Con el reloj inyectado, un caso planta el instante y mide el borde.
 *
 * ⚠️ **Y hay una consecuencia que se ve en pantalla:** el reloj que llega es el
 * **del navegador del operador**, no el del servidor. Con el reloj corrido, esto
 * puede decir «vencida» y el próximo mensaje **seguir en la misma sesión**, porque
 * el que decide es el servidor. Por eso `EstadoDeLaVentana` es **una derivación
 * declarada** y quien la dibuje tiene que decir de dónde salió.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⚠️ ③ `VENTANA_MS` ES UNA COPIA DECLARADA, IGUAL QUE `TIPO_TEXTO`
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `contracts/conversation/index.ts:1329` exporta
 * `VENTANA_DE_SESION_24H_MS = 24 * 60 * 60 * 1000` **como VALOR**, y
 * `apps/management-ui/vite.config.ts:10-13` **no tiene alias hacia `contracts/` a
 * propósito**: un import de valor desde ahí **rompe el build**. Es la misma
 * situación que `borde-simulador.ts` resolvió para `TIPO_TEXTO` y
 * `TIPO_INTERACTIVO`, y se resuelve igual: **se copia con la cita al lado y la
 * duplicación queda declarada**, en vez de romper el build.
 *
 * ⛔ **El día que el contrato cambie la ventana, esto sigue compilando y deja de
 * decir la verdad.** Hueco declarado, dueño `web-dev` + `apps-integrator` — el
 * mismo que `borde-simulador.ts` §② ya lleva.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔⛔ ④ `AW29` · EL ANCLAJE ES EL ÚLTIMO MENSAJE **NO-SISTEMA**, Y ANTES ESTABA MAL
 * ════════════════════════════════════════════════════════════════════════════
 *
 * ⭐ **RASTRO `[28/8/2026]` — ACÁ DECÍA QUE EL ANCLAJE ERA «LA ÚLTIMA LÍNEA DE LA
 * SESIÓN», Y ESO VENÍA DEL SPEC.** `simulador-conversacional.md:38` (`AW19`) dice
 * textual: *«se calcula con el `fecha_hora` de la última fila de la sesión»*.
 * **El motor ancla en otro lado**: `modules/conversation/src/sesion/ventana.ts:122`
 * es `ruedaElAnclaje: !esSistema` — **un mensaje del bot NUNCA mueve el anclaje**
 * (`:9-19`). ⇒ no fue un desvío del implementador: **la fuente estaba mal**, y por
 * eso `AW19` se enmienda en su lugar con el rastro (`ADR-0082`) y la frase vieja se
 * cita en vez de borrarse.
 *
 * ⭐ **El error tenía signo conocido**: la pantalla anclaba en la última línea
 * SALIENTE, y saliente **es el bot** (`apps/bot-web/src/entregas.ts:242`,
 * `filter(f => f.es_sistema)`). ⇒ el cartel **decía de más** cuando el bot contestó
 * después del usuario —el caso normal— y **de menos** cuando el usuario escribió y
 * el bot todavía no contestó.
 *
 * ⛔⛔ **Y con el cable de `AW16` el anclaje correcto es INALCANZABLE.**
 * `GET /entregas` viene **pre-filtrado a `es_sistema === true`** y `Entrega` **no
 * lleva `es_sistema`**. ⇒ el anclaje **no se puede derivar de lo que el simulador
 * recibe por ese camino**, y no por olvido: por construcción. Se deriva de
 * `GET conversaciones` —`FilaDeMensaje` sí trae `es_sistema`—, que es el cable que
 * `AW28` trajo. **Por eso `AW29` depende de `AW28` y no al revés.**
 *
 * ⚠️ **Decisión del dueño `[28/8/2026]`: lo deriva la PANTALLA, no el motor.** Que
 * el backend devolviera `anclaje` o `vence_en` ya calculado es más correcto
 * conceptualmente y **cruza a `contracts/`**, arrastrando gates. Se eligió lo barato
 * **con el costo dicho**: sigue siendo una derivación del navegador, y el cartel de
 * «lo calcula tu navegador» **no se saca**.
 *
 * ⛔ **Una sesión SIN ninguna línea de usuario no tiene anclaje, y eso no es cero ni
 * «ahora».** Es su propio caso —`sin-anclaje`—, porque las dos salidas cómodas
 * mienten: anclar en la última saliente es **el defecto que esta etapa corrige**, y
 * tratarla como «sin mensajes» taparía que la sesión existe.
 */

/**
 * La ventana de sesión. **Copia declarada** de
 * `contracts/conversation/index.ts:1329` — ver ③ del encabezado.
 */
export const VENTANA_MS = 24 * 60 * 60 * 1000

/**
 * Lo mínimo que una línea del hilo tiene que traer para poder agruparse.
 *
 * ⭐ Deliberadamente **más chico** que lo que las dos fuentes devuelven: acá entran
 * tanto las filas de `GET conversaciones` como las entregas de `GET /entregas`, y
 * las dos traen `id_sesion` y `fecha_hora`. Pedir menos es lo que permite que este
 * módulo no sepa de qué lado vino cada línea.
 */
export interface LineaDelHilo {
  readonly id_sesion: string
  /** ISO-8601, tal como viaja por el cable. */
  readonly fecha_hora: string
  /**
   * ⭐⭐ `AW29` · **Del sistema (el bot) o del usuario.** Es lo que decide el
   * ANCLAJE de la ventana, y por eso es obligatorio: ver ④ del encabezado.
   *
   * Va con el nombre del contrato —`es_sistema`, `FilaDeMensaje`— y sin traducir,
   * por lo mismo que `FilaDelPadron` no traduce los suyos: un nombre distinto en
   * esta capa sería el lugar donde alguien escribe una segunda opinión sobre qué
   * mueve el anclaje.
   */
  readonly es_sistema: boolean
}

/**
 * El estado de la sesión ACTUAL, derivado contra el reloj que se pasó.
 *
 * ⛔ **`vencida` NO significa «no se puede continuar».** Significa: *el próximo
 * mensaje va a abrir una sesión nueva*. Ver ① del encabezado.
 */
export type EstadoDeLaVentana =
  | { readonly caso: "sin-mensajes" }
  /**
   * ⭐ `AW29` · **Hay sesión, pero ninguna línea de usuario en lo que la pantalla
   * leyó.** No hay anclaje que medir y **no se sustituye por la última saliente**:
   * ésa es exactamente la derivación equivocada que esta etapa corrige.
   */
  | { readonly caso: "sin-anclaje" }
  | { readonly caso: "abierta"; readonly restanteMs: number }
  | { readonly caso: "vencida"; readonly desdeMs: number }

/** Una sesión: sus líneas en orden, y su `id_sesion`. */
export interface Sesion<T extends LineaDelHilo> {
  readonly id_sesion: string
  readonly lineas: readonly T[]
  /**
   * El instante de la última línea, sea de quien sea.
   *
   * ⛔⛔ **NO es el anclaje de la ventana** — ver ④ del encabezado. Lo único para lo
   * que sirve es **ORDENAR las sesiones entre sí**, que es una pregunta distinta:
   * cuál es la más reciente lo decide cuándo se escribió lo último, lo escriba quien
   * lo escriba. El nombre se conserva y la frase vieja —«Es el ANCLAJE de la
   * ventana»— se cita acá en vez de borrarse, porque un lector que llegue con ella
   * en la cabeza tiene que encontrar qué la levantó.
   */
  readonly ultima: number
  /**
   * ⭐⭐ `AW29` · **El ANCLAJE: el instante de la última línea NO-sistema.**
   *
   * `ventana.ts:122` es `ruedaElAnclaje: !esSistema`. `null` cuando la sesión no
   * tiene ninguna línea de usuario en lo que la pantalla leyó — y entonces **no hay
   * anclaje**, no un anclaje de repuesto.
   */
  readonly anclaje: number | null
}

function instante(linea: LineaDelHilo): number {
  const t = Date.parse(linea.fecha_hora)
  // ⚠️ Una fecha ilegible NO se convierte en `0` ni en «ahora»: las dos mienten,
  //    y la segunda haría aparecer una sesión abierta que no existe. Se manda al
  //    final, donde es visible, y no arrastra el anclaje hacia atrás.
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t
}

/**
 * Agrupa las líneas por `id_sesion`, **preservando el orden cronológico** dentro de
 * cada sesión y entre sesiones.
 *
 * ⛔ **No se infiere la sesión por el tiempo.** `id_sesion` lo asigna el módulo AL
 * ESCRIBIR y es inmutable (`contracts/conversation` ⟦la-sesion-se-asigna-al-escribir⟧);
 * recalcularlo acá sería **una segunda opinión** sobre algo que el motor ya dijo, y
 * discreparía justo en el borde, que es donde importa.
 */
export function agruparEnSesiones<T extends LineaDelHilo>(lineas: readonly T[]): readonly Sesion<T>[] {
  const porSesion = new Map<string, T[]>()
  for (const l of lineas) {
    const ya = porSesion.get(l.id_sesion)
    if (ya === undefined) porSesion.set(l.id_sesion, [l])
    else ya.push(l)
  }

  const sesiones: Sesion<T>[] = []
  for (const [id_sesion, propias] of porSesion) {
    const ordenadas = [...propias].sort((a, b) => instante(a) - instante(b))
    const ultimaLinea = ordenadas[ordenadas.length - 1]
    /*
     * ⭐⭐ `AW29` · EL ANCLAJE: la última línea **NO-sistema**, y nada más.
     * `ventana.ts:122` es `ruedaElAnclaje: !esSistema` — un mensaje del bot no lo
     * mueve. Se recorre desde el final porque `ordenadas` ya está en orden.
     */
    let anclaje: number | null = null
    for (let i = ordenadas.length - 1; i >= 0; i--) {
      const l = ordenadas[i]
      if (l !== undefined && !l.es_sistema) {
        anclaje = instante(l)
        break
      }
    }
    sesiones.push({
      id_sesion,
      lineas: ordenadas,
      ultima: ultimaLinea === undefined ? Number.POSITIVE_INFINITY : instante(ultimaLinea),
      anclaje,
    })
  }

  return sesiones.sort((a, b) => a.ultima - b.ultima)
}

/**
 * El estado de la ventana de la sesión ACTUAL —la última— contra el reloj dado.
 *
 * ⛔ `ahora` es un argumento y no se lee de ningún lado. Ver ② del encabezado.
 */
export function estadoDeLaVentana(
  sesiones: readonly Sesion<LineaDelHilo>[],
  ahora: Date,
  ventanaMs: number = VENTANA_MS,
): EstadoDeLaVentana {
  const actual = sesiones[sesiones.length - 1]
  if (actual === undefined) return { caso: "sin-mensajes" }

  /*
   * ⭐⭐ `AW29` · SE MIDE DESDE EL ANCLAJE, NO DESDE LA ÚLTIMA LÍNEA. Ver ④ del
   * encabezado: el bot no mueve el anclaje, así que anclar en la última línea decía
   * de MÁS cuando el bot contestó después del usuario —el caso normal—.
   */
  if (actual.anclaje === null) return { caso: "sin-anclaje" }

  const transcurrido = ahora.getTime() - actual.anclaje

  // ⭐ El `>` estricto es el mismo que `modules/conversation/src/sesion/ventana.ts`:
  //    exactamente en el límite la ventana sigue ABIERTA. Copiar la comparación con
  //    `>=` correría el borde un milisegundo respecto del motor, y esa discrepancia
  //    sólo se vería en el único instante donde alguien la está mirando.
  if (transcurrido > ventanaMs) return { caso: "vencida", desdeMs: transcurrido - ventanaMs }
  return { caso: "abierta", restanteMs: ventanaMs - transcurrido }
}
